/**
 * Runtime Contract Validator
 * Authority: INVARIANT AGENT (Stability V2)
 * Purpose: Detect drift between API/Adapter output and Render Laws.
 */

import { UI_INVARIANTS } from "../config/ui-invariants";

const TRUTH_LOG_KEY = 'ACCREZEO_RUNTIME_TRUTH';

export const RuntimeContractValidator = {
    /**
     * Log a contract violation
     */
    logViolation: (failureClass, moduleName, evidence) => {
        const currentPhase = UI_INVARIANTS.STATE.getPhase();
        const log = JSON.parse(localStorage.getItem(TRUTH_LOG_KEY) || '[]');
        const entry = {
            timestamp: new Date().toISOString(),
            class: failureClass,
            phase: currentPhase,
            module: moduleName,
            evidence: evidence
        };
        log.push(entry);
        localStorage.setItem(TRUTH_LOG_KEY, JSON.stringify(log.slice(-100)));

        console.warn(`[CONTRACT_VIOLATION] [${currentPhase}] ${failureClass} in ${moduleName}:`, evidence);

        // Blocking Logic: Only block if the phase allows it
        if (UI_INVARIANTS.STATE.shouldBlock(currentPhase)) {
            const errorMsg = `CRITICAL_INVARIANT_LOCK [${currentPhase}]: ${failureClass} in ${moduleName}`;
            console.error(errorMsg);
            throw new Error(errorMsg);
        }
    },

    /**
     * Validate an entry against a set of required keys
     */
    validateEntry: (entry, contract, moduleName) => {
        if (!entry) {
            RuntimeContractValidator.logViolation('NULL_ENTRY', moduleName, 'Received null/undefined entry');
            return false;
        }

        const missingFields = Object.keys(contract).filter(key => {
            return entry[key] === undefined || entry[key] === null;
        });

        if (missingFields.length > 0) {
            RuntimeContractValidator.logViolation(
                'FIELD_MISSING',
                moduleName,
                `Fields missing: ${missingFields.join(', ')}`
            );
            return false;
        }

        // Shape/Type Check (Minimal)
        for (const [key, defaultValue] of Object.entries(contract)) {
            if (typeof entry[key] !== typeof defaultValue) {
                RuntimeContractValidator.logViolation(
                    'UNEXPECTED_SHAPE',
                    moduleName,
                    `Field "${key}" expected type ${typeof defaultValue}, got ${typeof entry[key]}`
                );
            }
        }

        return true;
    },

    /**
     * Validate a collection of entries
     */
    validateCollection: (collection, contract, moduleName) => {
        if (!Array.isArray(collection)) {
            RuntimeContractValidator.logViolation('UNEXPECTED_SHAPE', moduleName, 'Expected array, got ' + typeof collection);
            return false;
        }

        if (collection.length === 0) return true; // Empty is valid shape

        // Validate a sample (1st item) to avoid overhead in large lists
        return RuntimeContractValidator.validateEntry(collection[0], contract, moduleName);
    }
};
